/**
 * VoiceLeading.js
 * Minimizes pitch movement between chords for smoother harmony.
 * Implements Horizontal Voice Leading.
 */

const VOICE_LEADING_CONFIG = {
    cluster: {
        standard: [0, 1, 2, 4, 6], // Standard Avant-Garde Cluster
        dynamic: [0, 2, 3, 5, 6]   // Wider/Dissonant
    }
};

// Voice Ranges (SATB approx)
const RANGES = {
    S: { min: 60, max: 81 },
    A: { min: 53, max: 74 },
    T: { min: 46, max: 67 },
    B: { min: 36, max: 57 }
};

/**
 * Applies Linear Voice Leading to an entire progression.
 * Minimizes total movement distance while respecting voice ranges.
 */
export const applyVoiceLeading = (progression, strictness = 'classic', traits = {}) => {
    if (!progression || progression.length === 0) return [];

    // Clash Detection Logic
    const genre = traits.name || '';
    const allowClash = genre.includes('Horror') || genre.includes('Jazz') || genre.includes('Avant-Garde');
    const keyRootPC = traits.keyRootPC !== undefined ? traits.keyRootPC : (traits.key ? ["C", "C#", "D", "D#", "E", "F", "F#", "G", "G#", "A", "A#", "B"].indexOf(traits.key) : 0);

    // State for linear continuity
    let prevNotes = null;

    return progression.map((chord, idx) => {
        if (!chord.notes || chord.notes.length === 0) return chord;

        let currentNotes = [...chord.notes];

        // 1. Initial Range Constraint (First Chord)
        // If first chord, just ensure it sits nicely in the middle
        if (idx === 0) {
            // Sort for consistent voice assignment
            prevNotes = currentNotes.sort((a,b) => a-b);
            return chord;
        }

        // 2. Linear minimization
        // For each note in current, find the nearest octave relative to the *corresponding* voice in prev

        let adjustedNotes = [];

        // Ensure strictly sorted for voice mapping
        currentNotes.sort((a,b) => a-b);

        if (prevNotes && prevNotes.length === currentNotes.length) {
            // Strict Voice Matching (S->S, A->A, etc.)
            adjustedNotes = currentNotes.map((note, vIdx) => {
                const pc = note % 12;
                const target = prevNotes[vIdx];

                // Find nearest octave to target
                const candidates = [
                    pc + (Math.floor(target/12)-1)*12,
                    pc + Math.floor(target/12)*12,
                    pc + (Math.floor(target/12)+1)*12
                ];

                // Sort by distance to target, but penalize Stagnation
                candidates.sort((a,b) => {
                    const distA = Math.abs(a - target);
                    const distB = Math.abs(b - target);

                    let penaltyA = 0;
                    let penaltyB = 0;

                    // Stagnation Penalty: If note repeats exactly, penalize it slightly to encourage movement
                    // ONLY if there are other valid options close by.
                    if (a === target) penaltyA = 2.0; // Artificial distance of 2 semitones
                    if (b === target) penaltyB = 2.0;

                    return (distA + penaltyA) - (distB + penaltyB);
                });

                return candidates[0];
            });
        } else {
            // Fallback: Centroid matching if voice counts differ
            const prevAvg = prevNotes.reduce((a, b) => a + b, 0) / prevNotes.length;
            adjustedNotes = currentNotes.map(note => {
                const pc = note % 12;
                const idealOctave = Math.round((prevAvg - pc) / 12);
                return pc + (idealOctave * 12);
            });
        }

        // --- 2.5 TONAL GRAVITY (Tendency Tone Resolution) ---
        // If the previous chord had a tendency tone (e.g. 7th), force resolution
        if (idx > 0 && prevNotes.length === adjustedNotes.length) {
            const prevChord = progression[idx-1];

            // 1. Explicit Guide Tones (From HarmonyManager)
            if (prevChord.guideTones) {
                Object.entries(prevChord.guideTones).forEach(([pitchStr, direction]) => {
                    const pitch = parseInt(pitchStr); // 0-11
                    // Find voice with this pitch class
                    const voiceIdx = prevNotes.findIndex(n => (n % 12) === pitch);
                    if (voiceIdx !== -1) {
                        const prevNote = prevNotes[voiceIdx];
                        const idealTarget = (direction === 'up') ? prevNote + 1 : prevNote - 1; // Semitone Step

                        const currentNote = adjustedNotes[voiceIdx];
                        const currentPC = currentNote % 12;

                        const candidates = [
                            currentPC + (Math.floor(idealTarget/12)-1)*12,
                            currentPC + Math.floor(idealTarget/12)*12,
                            currentPC + (Math.floor(idealTarget/12)+1)*12
                        ];
                        candidates.sort((a,b) => Math.abs(a-idealTarget) - Math.abs(b-idealTarget));
                        adjustedNotes[voiceIdx] = candidates[0];
                    }
                });
            }
            // 2. Implicit Logic (Fallback)
            // Check for Dominant 7th -> Resolution
            else if (prevChord.type === 'dom7' || prevChord.type === '7th' || prevChord.type === 'min7') {
                 // Dom7/Min7: 7th is Root + 10 (m7)
                 const seventhInterval = 10;
                 const prevRootPC = prevChord.rootPitchClass % 12;
                 const seventhPC = (prevRootPC + seventhInterval) % 12;

                 // Find which voice had the 7th
                 const voiceIdx = prevNotes.findIndex(n => (n % 12) === seventhPC);

                 if (voiceIdx !== -1) {
                     // This voice SHOULD resolve down (stepwise)
                     const prevNote = prevNotes[voiceIdx];
                     const idealTarget = prevNote - 1; // Down a semitone (or 2)

                     // Find the chord tone in THIS chord that is closest to idealTarget
                     const currentNote = adjustedNotes[voiceIdx]; // The note chosen by linear min
                     const currentPC = currentNote % 12;

                     // Generate candidates for this PC
                     const candidates = [
                        currentPC + (Math.floor(idealTarget/12)-1)*12,
                        currentPC + Math.floor(idealTarget/12)*12,
                        currentPC + (Math.floor(idealTarget/12)+1)*12
                     ];

                     // Pick the one closest to IDEAL TARGET (Gravity)
                     // Bias slightly: If distance is equal, prefer the one that is LOWER (Gravity)
                     candidates.sort((a,b) => {
                         const distA = Math.abs(a - idealTarget);
                         const distB = Math.abs(b - idealTarget);
                         if (Math.abs(distA - distB) < 0.1) return (a - idealTarget) - (b - idealTarget);
                         return distA - distB;
                     });

                     adjustedNotes[voiceIdx] = candidates[0];
                 }
            }

            // Check for Leading Tone of Global Key (resolving to Tonic)
            if (keyRootPC !== -1 && prevChord.type !== 'diminished') { // Diminished is messy
                const leadingTonePC = (keyRootPC + 11) % 12;

                // Find voice with Leading Tone
                const voiceIdx = prevNotes.findIndex(n => (n % 12) === leadingTonePC);

                if (voiceIdx !== -1) {
                    // Leading Tone wants to go UP to Tonic
                    const prevNote = prevNotes[voiceIdx];
                    const idealTarget = prevNote + 1; // Up semitone

                    const currentNote = adjustedNotes[voiceIdx];
                    // Only enforce if the current chord actually HAS the tonic?
                    // If current chord is V again, LT might hold.
                    // Only if current chord Root is Tonic (I)?
                    // Or if current chord contains Tonic pitch.

                    const currentPC = currentNote % 12;
                    if (currentPC === keyRootPC) { // Resolving to Tonic Note
                         // Snap to ideal target (octave)
                         const candidates = [
                            currentPC + (Math.floor(idealTarget/12)-1)*12,
                            currentPC + Math.floor(idealTarget/12)*12,
                            currentPC + (Math.floor(idealTarget/12)+1)*12
                         ];
                         candidates.sort((a,b) => Math.abs(a-idealTarget) - Math.abs(b-idealTarget));
                         adjustedNotes[voiceIdx] = candidates[0];
                    }
                }
            }
        }

        adjustedNotes.sort((a, b) => a - b);

        // 3. Strictness Logic: Parallel Fifths check
        if (strictness === 'strict' || strictness === 'bach_strict') {
            if (prevNotes.length > 1 && adjustedNotes.length > 1) {
                const prevBass = prevNotes[0];
                const prevSop = prevNotes[prevNotes.length-1];
                const currBass = adjustedNotes[0];
                const currSop = adjustedNotes[adjustedNotes.length-1];

                const prevInt = (prevSop - prevBass) % 12;
                const currInt = (currSop - currBass) % 12;

                const isParallel = (currBass - prevBass) * (currSop - prevSop) > 0; // Same direction

                if (isParallel && prevInt === 7 && currInt === 7) {
                    // Fix Parallel 5th: Break parallel motion by shifting soprano octave
                    adjustedNotes[adjustedNotes.length-1] -= 12;
                    adjustedNotes.sort((a, b) => a - b);
                }
            }
        }

        // 4. CLASH DETECTION (Vertical)
        if (!allowClash && adjustedNotes.length > 1) {
            for (let i = 0; i < adjustedNotes.length - 1; i++) {
                const n1 = adjustedNotes[i];
                const n2 = adjustedNotes[i+1];
                const interval = n2 - n1;

                if (interval === 1) {
                    // Minor 2nd Clash!
                    // Check if it's a valid scale interval or extension (e.g. maj7)
                    // If it's 7th and Root, usually 7th is below Root?
                    // E.g. B and C. (B-C is m2). In CMaj7, B is 7th.
                    // If spread out (B3, C4), no clash.
                    // If close (B3, C4), clash.
                    // Jazz allows it. Classical avoids it.
                    // Shift n2 up an octave unless it's a specific tension we want?
                    // Safe default: Shift.
                    adjustedNotes[i+1] += 12;
                }
            }
            adjustedNotes.sort((a, b) => a - b);
        }

        prevNotes = adjustedNotes;
        return { ...chord, notes: adjustedNotes };
    });
};

/**
 * GENRE-SPECIFIC VOICING STRATEGIES (Vertical)
 * Called *before* Horizontal Voice Leading.
 */
export const applyGenreVoicing = (notes, strategy, freedom = 0.5) => {
    if (!notes || notes.length === 0) return notes;
    const sorted = [...notes].sort((a,b) => a - b);
    const root = sorted[0];

    // Task 3: Harmonic Sprawl (Freedom = Spread)
    // Inject spread logic based on harmonicFreedom if available in strategy (as 'freedom' prop or inferred)
    // Strategy might be a string (e.g. 'standard') or object.

    // If strategy is string, we might want to override based on context?
    // The current signature is (notes, strategy). We don't have context here.
    // Plan requires passing harmonicComplexity from GenerationPipeline.
    // 'applyVoicing' in MusicEngine calls this.
    // Let's modify 'applyVoicing' in MusicEngine to pass a composite strategy object if needed, or handle it here if 'strategy' contains the value.

    // Assuming 'strategy' can be an object with { type: 'standard', freedom: 0.5 }
    // Or we handle it via override logic below.

    // For now, let's implement the logic assuming strategy might contain 'freedom'
    // If strategy is string, we can't access freedom unless we changed the call signature.
    // Let's check MusicEngine... it calls: `applyGenreVoicing(voicedNotes, strategy)`.
    // I need to update MusicEngine to pass the freedom value.
    // I will assume MusicEngine will be updated to pass an object like { name: 'standard', freedom: 0.8 }
    // OR I can overload the function signature to accept (notes, strategy, freedom).

    // Let's check how `applyGenreVoicing` is used.
    // `MusicEngine.js` line ~490: `applyGenreVoicing(voicedNotes, strategy)`

    // I will modify `applyGenreVoicing` signature to `(notes, strategy, freedom = 0.5)`

    // ... wait, I need to update MusicEngine too.

    // DNA Profile Logic (High Priority)
    // If 'strategy' is an object, treat it as a Voicing Profile
    if (typeof strategy === 'object' && strategy.spread) {
        const spread = strategy.spread; // e.g. [0, 7, 12, 16]
        const doublings = strategy.doublings || []; // e.g. ['root', 'fifth']

        const result = [];

        // 1. Apply Spread (Intervals from Root)
        spread.forEach(interval => {
            result.push(root + interval);
        });

        // 2. Apply Doublings (Add extra notes)
        // Need to identify scale degree of original notes?
        // Simpler: Just map 'root', 'third', 'fifth' to offsets
        const rootPC = root % 12;

        doublings.forEach(d => {
            if (d === 'root') result.push(root + 12); // Add octave
            if (d === 'fifth') result.push(root + 19); // Add 5th (octave+5th)
            if (d === 'third') {
                // Find 3rd quality from original notes?
                // Heuristic: Check if m3 (3) or M3 (4) exists in original notes
                const hasMin3 = notes.some(n => (n%12) === (rootPC+3)%12);
                const hasMaj3 = notes.some(n => (n%12) === (rootPC+4)%12);
                if (hasMin3) result.push(root + 15); // m3 + 8va
                else if (hasMaj3) result.push(root + 16); // M3 + 8va
            }
        });

        // Deduplicate
        const uniqueResult = [...new Set(result)];
        return uniqueResult.sort((a,b) => a - b);
    }

    // 1. HORROR / CLUSTER
    // Stack semitones/tones around the root
    if (strategy === 'cluster' || strategy === 'dynamic_cluster') {
        const intervals = (strategy === 'dynamic_cluster') ? VOICE_LEADING_CONFIG.cluster.dynamic : VOICE_LEADING_CONFIG.cluster.standard;
        return intervals.map(i => root + i);
    }

    // 2. CINEMATIC / OPEN
    // Wide intervals: Root, 5th, Octave, 3rd (high)
    // Avoid low 3rds (mud)
    if (strategy === 'open') {
        const result = [root];
        // Add Sub-Octave (Essential for Epic Lows)
        result.push(root - 12);

        // Add 5th
        result.push(root + 7);
        // Add Octave
        result.push(root + 12);

        // Add 3rd (Octave up)
        const third = sorted.find(n => (n % 12) === (root + 4) % 12 || (n % 12) === (root + 3) % 12);
        if (third) {
            // Push 3rd up 1 or 2 octaves
            // We assume 'third' comes from input (close position)
            // Move it up to open the texture
            const thirdPC = third % 12;
            const rootPC = root % 12;
            let offset = thirdPC - rootPC;
            if (offset < 0) offset += 12;
            // result is root + 12 + offset
            result.push(root + 12 + offset);
        }

        // Add High 5th (Sparkle)
        result.push(root + 19);

        return [...new Set(result)].sort((a,b) => a - b);
    }

    // 3. ROMANTIC / CLOSE
    // Lush: Double the 3rd, keep close intervals in middle register
    if (strategy === 'close') {
        // [Root, 3rd, 5th, 7th, 3rd(+8)]
        const result = [...sorted];
        // Double the 3rd if exists
        const third = sorted.find(n => (n % 12) === (root + 4) % 12 || (n % 12) === (root + 3) % 12);
        if (third) result.push(third + 12);
        return result.sort((a,b) => a - b);
    }

    // --- Task 3: Harmonic Sprawl (Freedom Logic) ---
    // Low Freedom (< 0.3): Close Position (Cluster within 1 octave)
    // High Freedom (> 0.7): Open Position / Drop-2 (Spread > 1.5 octaves)
    if (freedom < 0.3) {
        // Force Close (Compact)
        const result = [...sorted];
        // Ensure span is < 12 semitones if possible.
        // If > 12, collapse top note down an octave?
        // Simple heuristic: Drop 2nd from top down an octave if > 1 octave span.
        if (result[result.length-1] - result[0] > 12) {
             // Compact: Move top note down octave
             // result[result.length-1] -= 12; // Might cause collision?
             // Safer: Move notes to be as close to root as possible.
             return result.map(n => {
                 // Fold to within [root, root+12]
                 const pc = n % 12;
                 const rootPC = root % 12;
                 let diff = pc - rootPC;
                 if (diff < 0) diff += 12;
                 return root + diff;
             }).sort((a,b)=>a-b);
        }
        return result;
    }
    else if (freedom > 0.7) {
        // Force Open (Sprawl)
        // Drop-2: Take the 2nd highest note and drop it an octave
        const result = [...sorted];
        if (result.length >= 4) {
            // Drop-2
            const secondHighest = result[result.length - 2];
            result[result.length - 2] = secondHighest - 12;
        } else if (result.length === 3) {
            // Drop middle note
            result[1] -= 12;
        }
        return result.sort((a,b) => a - b);
    }

    // 4. IMPRESSIONIST / PLANING
    // Constant Structure: m9 or 9th chord on the root, regardless of scale.
    // E.g. Root, 5th, m7, 9
    if (strategy === 'impressionist_planing') {
        // Force specific intervals relative to root
        return [
            root,           // Root
            root + 7,       // 5th
            root + 10,      // m7
            root + 14,      // 9
            root + 16       // 3rd (Major) - wait, dominant 9?
            // Let's do m9: Root, m3, 5, m7, 9
            // R, R+3, R+7, R+10, R+14
        ].sort((a,b) => a - b);
    }

    // Default
    return notes;
};

/**
 * LOW INTERVAL LIMIT (Physics of Sound)
 * Ensures no muddy intervals in the low register.
 * @param {Array} notes - MIDI note numbers
 * @param {boolean} strict - Whether to strictly enforce standard limits
 */
export const checkLowIntervalLimits = (notes, strict = true) => {
    // TODO [TYPE: THEORY] constrain pitch generation to the realistic Tessitura of this specific instrument.
    if (!notes || notes.length < 2) return notes;

    // Limits:
    // Below C3 (48): No intervals smaller than a 5th (7 semitones)
    // Below C2 (36): No intervals smaller than an Octave (12 semitones)

    const sorted = [...notes].sort((a,b) => a - b);
    const cleaned = [sorted[0]]; // Keep lowest note

    for (let i = 1; i < sorted.length; i++) {
        const lower = cleaned[cleaned.length-1];
        const upper = sorted[i];
        const interval = upper - lower;

        let valid = true;

        if (strict) {
            if (lower < 36) { // Deep Bass
                if (interval < 12) valid = false; // Must be octave or more
            } else if (lower < 48) { // Low Bass
                if (interval < 7) valid = false; // Must be 5th or more
            } else if (lower < 55) { // Baritone
                if (interval < 3) valid = false; // No semitones/tones
            }
        }

        if (valid) {
            cleaned.push(upper);
        } else {
            // Fix: Move upper note up an octave?
            // Or just drop it?
            // Moving up preserves harmony.
            const shifted = upper + 12;
            cleaned.push(shifted);
        }
    }

    return cleaned.sort((a,b) => a - b);
};

/**
 * LEGATO PAD GLUE (Texture Cohesion)
 * Merges consecutive notes of the same pitch/instrument to create sustained textures.
 * Reduces "choppiness" in Pad/String roles.
 */
export const applyLegatoGlue = (events) => {
    if (!events || events.length === 0) return events;

    // Filter candidates: Pads, Strings, sustained Harmony
    // Logic: Group by Instrument ID + Pitch + Channel
    const groups = {};
    const others = [];

    events.forEach(ev => {
        const isPad = ev.role && (
            ev.role.includes('pad') ||
            ev.role.includes('harmony') ||
            ev.role.includes('drone') ||
            (ev.instrumentId && ev.instrumentId.includes('strings'))
        );
        // Exclude percussive/plucked if marked
        const isShort = ev.articulation === 'staccato' || ev.articulation === 'spiccato' || ev.articulation === 'pizzicato';

        if (isPad && !isShort && ev.d !== undefined) {
            const key = `${ev.instrumentId}_${ev.d}_${ev.channel}`;
            if (!groups[key]) groups[key] = [];
            groups[key].push(ev);
        } else {
            others.push(ev);
        }
    });

    const glued = [];

    Object.values(groups).forEach(group => {
        // Sort by time
        group.sort((a,b) => a.s - b.s);

        if (group.length === 0) return;

        const merged = [ { ...group[0] } ];

        for (let i = 1; i < group.length; i++) {
            const last = merged[merged.length - 1];
            const current = group[i];

            // Check connectivity
            // Tolerance: 0.05 beats
            const endOfLast = last.s + last.len;
            const diff = current.s - endOfLast;

            // If contiguous (or slight overlap/gap)
            if (Math.abs(diff) < 0.1) {
                // GLUE THEM
                // New length = (current.s + current.len) - last.s
                const newEnd = current.s + current.len;
                last.len = newEnd - last.s;
                // Merge velocity? Average them? Keep first attack?
                // Pads usually hold volume. Keep first.
                // If current has a significant velocity bump, maybe don't merge?
                // For now, simple glue.
            } else {
                // Gap too big, start new note
                merged.push({ ...current });
            }
        }
        glued.push(...merged);
    });

    return [...others, ...glued].sort((a,b) => a.s - b.s);
};

/**
 * THE EDITOR: Voice Leading Validator
 * Enforces strict musicality rules for harmony transitions.
 * @param {number} prevNote - MIDI pitch
 * @param {number} nextNote - MIDI pitch
 * @param {Object} context - { tension, role, key, scaleType, melodyDirection }
 * @returns {boolean} - True if valid, False if vetoed
 */
/**
 * DYNAMIC LEGATO LOGIC
 * Decides whether two consecutive notes should overlap (glide/legato) based on context.
 * @param {Object} noteA - The first note event
 * @param {Object} noteB - The second note event
 * @param {Object} instrument - The instrument playing these notes
 */
export const shouldAllowLegatoOverlap = (noteA, noteB, instrument) => {
    if (!noteA || !noteB) return false;

    const interval = Math.abs(noteB.d - noteA.d);

    // 1. Force Separation Check (Physics)
    // Large leaps cannot be smooth glides on most instruments
    if (interval > 12) return false;

    // 2. Articulation Intent
    const isLegato = (noteA.articulation === 'legato');

    // 3. Instrument Capability
    // Assuming 'instrument.capabilities' exists
    const supportsGlide = instrument?.capabilities?.glide || false;
    const isSynth = instrument?.id?.includes('synth') || false;
    const isString = instrument?.traits?.family === 'strings';

    // LOGIC MATRIX
    if (isLegato) {
        // Small intervals (Stepwise) -> Always overlap for Legato
        if (interval <= 2) return true;

        // Medium intervals (3rd, 4th) -> Overlap if String or Glide-capable
        if (interval <= 5) {
            if (isString || supportsGlide || isSynth) return true;
        }

        // Larger intervals -> Only if explicit Glide support
        if (supportsGlide) return true;
    }

    return false;
};

export const validateVoiceLeading = (prevNote, nextNote, context) => {
    if (prevNote === null || prevNote === undefined) return true;

    const interval = nextNote - prevNote;
    const absInterval = Math.abs(interval);
    const tension = context.tension || 0.0;
    const role = context.role || 'harmony';

    // 1. GRAVITY RULE
    // Veto intervals > 5 semitones (Perfect 4th) unless High Tension or Bass
    if (absInterval > 5) {
        if (role === 'bass') return true; // Bass allowed to leap
        if (tension > 0.8) return true;   // Chaos allowed
        return false; // VETO
    }

    // 2. RESOLUTION RULE (Tendency Tones)
    // If prevNote was Leading Tone (7th), must resolve to Tonic (Root) or hold
    if (context.key && context.scaleType) {
        const rootIdx = ["C", "C#", "D", "D#", "E", "F", "F#", "G", "G#", "A", "A#", "B"].indexOf(context.key);
        // Calculate Leading Tone (Major 7th or scale degree 7)
        // For Major/Harmonic Minor, it's Root-1 (11 semitones)
        // Note: In Minor keys (Aeolian), it's flat 7 (10 semitones). We usually care about strict resolution in Leading Tone context (Harmonic Minor / Major).
        // Let's stick to Major/Harmonic Minor definition (11 semitones) for now, or check scaleType.
        const isLeadingToneScale = context.scaleType === 'major' || context.scaleType === 'harmonic_minor' || context.scaleType === 'melodic_minor';

        if (isLeadingToneScale && rootIdx !== -1) {
            const leadingTone = (rootIdx + 11) % 12;
            const prevPC = prevNote % 12;

            if (prevPC === leadingTone) {
                const nextPC = nextNote % 12;
                const isTonic = nextPC === rootIdx;
                const isHold = nextPC === prevPC;

                // Strictness depends on Tension? Low tension = strict resolution.
                if (tension < 0.6) {
                    if (!isTonic && !isHold) return false; // VETO
                }
            }
        }
    }

    // 3. INERTIA RULE (Contrary Motion)
    // If melody is moving UP, harmony should preferably move DOWN or STATIC
    // This is a soft rule (preference), but we can veto extreme parallel motion
    if (context.melodyDirection) {
        // Parallel motion check: if melody went UP and harmony goes UP > 2 semitones
        if (context.melodyDirection === 'up' && interval > 2) return false;
        // If melody went DOWN and harmony goes DOWN < -2 semitones
        if (context.melodyDirection === 'down' && interval < -2) return false;
    }

    return true;
};
